import { ChangeDetectionStrategy, Component, computed, input, model } from '@angular/core';
import {
  FIELD_BORDER,
  FIELD_BORDER_ERROR,
  FIELD_ERROR,
  FIELD_HINT,
  FIELD_INPUT,
  FIELD_LABEL,
} from './field-styles';
import { uniqueId } from './ids';

export interface SelectOption {
  readonly value: string;
  readonly label: string;
}

/**
 * Elenco a discesa nativo con etichetta, aiuto ed errore: il `<select>` resta dentro la
 * libreria. Con `hideLabel` l'etichetta serve solo ai lettori di schermo (per esempio in una
 * riga di tabella).
 */
@Component({
  selector: 'bs-select',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'flex flex-col gap-1.5' },
  template: `
    <label [for]="id" [class]="hideLabel() ? 'sr-only' : labelClass">{{ label() }}</label>
    <select
      [id]="id"
      [class]="selectClass()"
      [disabled]="disabled()"
      [attr.aria-invalid]="error() ? 'true' : null"
      [attr.aria-describedby]="describedBy()"
      [attr.data-testid]="testId()"
      (change)="onChange($event)"
    >
      @for (option of options(); track option.value) {
        <option [value]="option.value" [selected]="option.value === value()">
          {{ option.label }}
        </option>
      }
    </select>
    @if (hint(); as text) {
      <p [id]="hintId" [class]="hintClass">{{ text }}</p>
    }
    @if (error(); as message) {
      <p [id]="errorId" [class]="errorClass">{{ message }}</p>
    }
  `,
})
export class SelectComponent {
  readonly label = input.required<string>();
  readonly options = input.required<readonly SelectOption[]>();
  readonly value = model<string | null>(null);
  readonly hint = input<string | null>(null);
  readonly error = input<string | null>(null);
  readonly disabled = input(false);
  readonly hideLabel = input(false);
  readonly testId = input<string | null>(null);

  protected readonly id = uniqueId('bs-select');
  protected readonly hintId = `${this.id}-hint`;
  protected readonly errorId = `${this.id}-error`;
  protected readonly labelClass = FIELD_LABEL;
  protected readonly hintClass = FIELD_HINT;
  protected readonly errorClass = FIELD_ERROR;
  protected readonly selectClass = computed(
    () => `${FIELD_INPUT} ${this.error() ? FIELD_BORDER_ERROR : FIELD_BORDER} cursor-pointer`,
  );
  protected readonly describedBy = computed(
    () =>
      [this.hint() ? this.hintId : null, this.error() ? this.errorId : null]
        .filter(Boolean)
        .join(' ') || null,
  );

  protected onChange(event: Event): void {
    this.value.set((event.target as HTMLSelectElement).value);
  }
}
